import { useEffect } from 'react';

/**
 * Custom hook to manage SEO meta tags and Open Graph data dynamically
 * Updates page title, meta descriptions, and social sharing tags
 */
export const useSEO = ({
  title = 'Stick Audio - AI Text-to-Speech Generator',
  description = 'Transform text into natural-sounding speech with AI-powered voices. Voice cloning, unlimited custom voices, and REST API access.',
  image = 'https://stick.audio/logo.png',
  url = 'https://stick.audio',
  type = 'website',
  keywords = '',
  author = 'Stick Audio',
}) => {
  useEffect(() => {
    // Update page title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
      }

      tag.setAttribute('content', content);
    };

    // SEO Meta Tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    updateMetaTag('author', author);

    // Open Graph Tags (Social Media)
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'Stick Audio', true);

    // Twitter Card Tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:card', 'summary_large_image');

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = url;

    // Update structured data
    const updateStructuredData = (data) => {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    // Basic Organization Schema
    updateStructuredData({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Stick Audio',
      description: description,
      url: url,
      image: image,
      applicationCategory: 'UtilityApplication',
    });
  }, [title, description, image, url, type, keywords, author]);
};

export default useSEO;
